import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/dialog";
import { Button } from "src/components/ui/button";
import BorderedSelect from "src/components/form-new/bordered-select";
import type { LoadCommissionInvoicesRequest } from "../types/commissions.types";

const QUARTER_OPTIONS = [
  { value: "Q1", label: "Q1 (Jan–Mar)" },
  { value: "Q2", label: "Q2 (Apr–Jun)" },
  { value: "Q3", label: "Q3 (Jul–Sep)" },
  { value: "Q4", label: "Q4 (Oct–Dec)" },
];

function getCurrentQuarter(): string {
  const month = new Date().getMonth() + 1;
  return `Q${Math.ceil(month / 3)}`;
}

function getYearsForLoad(): number[] {
  const currentYear = new Date().getFullYear();
  return [currentYear + 1, currentYear, currentYear - 1, currentYear - 2];
}

interface LoadCommissionInvoicesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: LoadCommissionInvoicesRequest) => Promise<void>;
}

const LoadCommissionInvoicesModal: React.FC<LoadCommissionInvoicesModalProps> = ({
  open,
  onOpenChange,
  onSubmit,
}) => {
  const yearOptions = useMemo(
    () => getYearsForLoad().map((y) => ({ value: String(y), label: String(y) })),
    []
  );
  const [quarter, setQuarter] = useState(getCurrentQuarter);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setQuarter(getCurrentQuarter());
      setYear(String(new Date().getFullYear()));
    }
  }, [open]);

  const handleSubmit = async() => {
    if (!quarter || !year || isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    try {
      await onSubmit({ quarter, year: Number(year) });
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = Boolean(quarter && year) && !isSubmitting;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent
        className="bg-white sm:max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle className="text-gray-900">
            Load commission invoices
          </DialogTitle>
          <DialogDescription className="text-gray-600">
            Select the quarter and year to load paid commission invoices from QuickBooks.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-2">
          <BorderedSelect
            label="Quarter"
            fullWidth
            containerMaxWidth="100%"
            options={QUARTER_OPTIONS}
            value={quarter}
            onChange={(e) => setQuarter(e.target.value)}
          />
          <BorderedSelect
            label="Year"
            fullWidth
            containerMaxWidth="100%"
            options={yearOptions}
            value={year}
            onChange={(e) => setYear(e.target.value)}
          />
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            className="rounded-full border-gray-300"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="black"
            className="rounded-full"
            disabled={!canSubmit}
            onClick={() => void handleSubmit()}
          >
            {isSubmitting ? "Loading…" : "Load"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LoadCommissionInvoicesModal;
